// Phone/DialSheet — Keypad + Contacts behind the Calls page's Dial button. A compact
// Modal on desktop, a bottom Sheet at phone width (Design Standard v1). Every call
// is placed through ClickToCallButton → the in-call overlay; nothing else dials.
import React, { useEffect, useState } from "react";
import { Delete } from "lucide-react";
import { searchCustomers } from "../../../api/customersApi";
import ClickToCallButton from "../../Voice/ClickToCallButton";
import Modal from "../../ui/Modal";
import Sheet from "../../ui/Sheet";
import { Input } from "../../ui/input";
import { TabBar } from "../../ui/tabs";

const KEYS = [
  ["1", ""], ["2", "ABC"], ["3", "DEF"],
  ["4", "GHI"], ["5", "JKL"], ["6", "MNO"],
  ["7", "PQRS"], ["8", "TUV"], ["9", "WXYZ"],
  ["*", ""], ["0", "+"], ["#", ""],
];

function Keypad() {
  const [value, setValue] = useState("");
  return (
    <div className="dial-pad">
      <div className="dial-pad__display">
        <Input
          className="dial-pad__input"
          value={value}
          onChange={(e) => setValue(e.target.value.replace(/[^\d*#+]/g, ""))}
          placeholder="Enter a number"
          inputMode="tel"
          aria-label="Number to dial"
        />
        {value ? (
          <button type="button" className="dial-pad__del" onClick={() => setValue((v) => v.slice(0, -1))} aria-label="Delete last digit">
            <Delete size={18} />
          </button>
        ) : null}
      </div>
      <div className="dial-pad__grid">
        {KEYS.map(([k, sub]) => (
          <button key={k} type="button" className="dial-pad__key" onClick={() => setValue((v) => v + k)}>
            <span className="dial-pad__digit">{k}</span>
            {sub ? <span className="dial-pad__letters">{sub}</span> : null}
          </button>
        ))}
      </div>
      <div className="dial-pad__call">
        <ClickToCallButton phone={value} label="Call" className="dial-pad__callbtn" />
      </div>
    </div>
  );
}

function Contacts() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setRows([]); return undefined; }
    let live = true;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await searchCustomers(q.trim());
        const d = res?.data ?? res;
        const list = Array.isArray(d) ? d : d?.results || [];
        if (live) setRows(list.slice(0, 30));
      } catch {
        if (live) setRows([]);
      } finally {
        if (live) setSearching(false);
      }
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [q]);

  return (
    <div className="dial-contacts">
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by name, phone or email"
        aria-label="Search contacts"
        autoFocus
      />
      {q.trim().length < 2 ? (
        <p className="dial-hint">Type a name or number to find someone to call.</p>
      ) : searching && !rows.length ? (
        <p className="dial-hint">Searching…</p>
      ) : !rows.length ? (
        <p className="dial-hint">No contact matches “{q.trim()}”.</p>
      ) : (
        <div className="dial-contacts__list">
          {rows.map((c) => {
            const name = c.full_name || [c.first_name, c.last_name].filter(Boolean).join(" ") || c.name || "Unnamed";
            const number = c.primary_phone || c.phone || c.phone_number || "";
            return (
              <div className="dial-contact" key={c.id || c.customer_id || number}>
                <span className="dial-contact__name">{name}</span>
                <span className="dial-contact__number">{number || "No number"}</span>
                {number ? <ClickToCallButton phone={number} calleeName={name} label="Call" /> : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const TABS = [{ id: "keypad", label: "Keypad" }, { id: "contacts", label: "Contacts" }];

export default function DialSheet({ open, onClose, isPhone }) {
  const [tab, setTab] = useState("keypad");
  if (!open) return null;
  const body = tab === "keypad" ? <Keypad /> : <Contacts />;
  if (isPhone) {
    return (
      <Sheet open onClose={onClose} title="Dial" className="dial-sheet">
        <TabBar tabs={TABS} value={tab} onChange={setTab} />
        <div className="dial-sheet__body">{body}</div>
      </Sheet>
    );
  }
  return (
    <Modal
      open
      onClose={onClose}
      title="Dial"
      size="compact"
      className="dial-modal"
      tabs={TABS.map((t) => ({ ...t, active: tab === t.id, onClick: () => setTab(t.id) }))}
    >
      {body}
    </Modal>
  );
}
